import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import ZoomControl from 'mapbox-gl-controls/lib/zoom';
import TooltipControl from 'mapbox-gl-controls/lib/tooltip';
import languageControl from '../Map/controls/LanguageControl';
import 'mapbox-gl-controls/theme.css';
import { syncFavorite } from '../helpers/favorites';
import { attachMapAnalytics, sendEvent } from '../helpers/analytics';
import { LAYERS, buildingIdMapFilter, getColor, initMapLayers } from '../Map/layers';
import contactsOpportunities from '../Contacts/ContactsOpportunities/ContactsOpportunities';
import { initDeveloperQuestion } from '../DeveloperQuestionPopup/DeveloperQuestionPopup';
import { initPhone } from '../ContactsActions/ContactsPhoneButton/ContactsPhoneButton';
import { getBuildingsDataByIds } from '../helpers/getBuildingsDataByIds';

class Map {
  constructor() {
    this.container = document.querySelector('#developer-map');
    if (!this.container) return;
    this.card = document.querySelector('#developer-card');
    this.cardRender = this.card.querySelector('[data-render]');
    this.opportunities = document.querySelector('#developer-opportunities');
    this.mouseEvents = ['mouseenter', 'mouseover', 'mousemove'];
    this.currentSalesCenterId = 0;
    this.salesCenterData = {};
    this.contactsCard = document.getElementById('developer-contacts-card');

    this.scrollListener = this.scrollListener.bind(this);
    this.openCard = this.openCard.bind(this);
    this.closeCard = this.closeCard.bind(this);
    this.openContactsCard = this.openContactsCard.bind(this);
    this.closeContactsCard = this.closeContactsCard.bind(this);
    this.buildingsIds = window.params.buildingsIds;
    this.buildingsData = [];
    window.addEventListener('scroll', this.scrollListener);
    this.attachCloseListener();
    this.scrollListener();
  }

  attachCloseListener() {
    document.addEventListener('click', (e) => {
      if (e.target.hasAttribute('data-close')) {
        this.closeCard();
      }
    });
  }

  fetchBuildingData() {
    getBuildingsDataByIds(this.buildingsIds, (buildingsData) => {
      this.buildingsData = buildingsData;
      this.colorize();
    });
  }

  mapInit() {
    if (!mapboxgl.supported()) return;
    this.map = new mapboxgl.Map({
      container: this.container,
      style: window.mapStreetsStyle,
      zoom: 10,
      center: [30.5234, 50.4501],
      bounds: window.params.buildingsBounds,
      fitBoundsOptions: {
        padding: 60,
        maxZoom: 16,
      },
      minZoom: 5,
      maxZoom: 18,
      scrollZoom: false,
      dragRotate: false,
    });

    this.map.addControl(languageControl);
    this.map.addControl(new ZoomControl(), 'bottom-right');
    if (!window.isTouch) {
      [LAYERS.points, LAYERS.contours_fill].forEach((layerId) => {
        const tooltipControl = new TooltipControl({
          layer: layerId,
          getContent: (event) => {
            const props = event.features[0].properties;
            const key = window.currentLanguage === 'ru' ? 'name:ru' : 'name:uk';
            return props[key];
          },
        });
        this.map.addControl(tooltipControl);
      });
    }
    attachMapAnalytics(this.container);

    this.map.on('style.load', () => {
      [LAYERS.points, LAYERS.contours_fill, LAYERS.sections].forEach((layerId) => {
        this.map.setFilter(layerId, [
          'in',
          buildingIdMapFilter,
          ['literal', window.params.buildingsIds],
        ]);
      });
      [LAYERS.points, LAYERS.contours_fill].forEach((layerId) => {
        this.map.on('click', this.closeCard);
        this.map.on('click', layerId, this.openCard);
      });
      initMapLayers(this.map);
      this.drawPins();
      this.map.on('click', () => {
        if (this.contactsCard) {
          this.closeContactsCard();
        }
      });
      this.fetchBuildingData();
    });
  }

  fetchBuilding(id) {
    fetch(`/api/card/${id}`, {
      method: 'post',
      headers: {
        'Content-type': 'application/json',
        'X-CSRF-Token': window.csrf,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        source: 'developer',
        language: window.currentLanguage,
      }),
    })
      .then((res) => res.text())
      .then((html) => {
        this.cardRender.innerHTML = html;
        syncFavorite();
        this.card.classList.add('-active');
      })
      .catch((error) => {
        console.warn(error);
      });
  }

  fetchContacts() {
    fetch(`/api/contacts-card/${this.currentSalesCenterId}`, {
      method: 'post',
      headers: {
        'Content-type': 'application/json',
        'X-CSRF-Token': window.csrf,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        source: 'developer',
        language: window.currentLanguage,
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        this.salesCenterData = json;
        this.contactsCard.innerHTML = this.salesCenterData.card;
        this.openContactsCard();
      })
      .catch((error) => {
        console.warn(error);
      });
  }

  colorize(selectedId = null) {
    [LAYERS.points, LAYERS.sections, LAYERS.contours_fill, LAYERS.contours_line].forEach(
      (layerId) => {
        const layer = this.map.getLayer(layerId);
        const colorProperty = `${layer.type}-color`;
        const colorValue = getColor(selectedId, {
          filtered: this.buildingsData,
          layerId,
          withMutedAreas: true,
        });
        this.map.setPaintProperty(layer.id, colorProperty, colorValue);
      },
    );
  }

  drawPins() {
    window.params.salesCenters.forEach((salesCenter) => {
      const marker = document.createElement('div');
      marker.className = 'ContactsLocation-marker';
      marker.dataset.salesCenterId = salesCenter.id;
      marker.addEventListener('click', (event) => {
        event.stopPropagation();
        this.closeContactsCard();
        this.currentSalesCenterId = salesCenter.id;
        this.fetchContacts();
        this.closeCard();
      });
      this.mouseEvents.forEach((mouseEvent) => {
        /* don't show LAYERS tooltips for markers */
        marker.addEventListener(mouseEvent, (e) => e.stopPropagation());
      });
      new mapboxgl.Marker({ element: marker, anchor: 'bottom' })
        .setLngLat(salesCenter.coordinates)
        .addTo(this.map);
    });
  }

  closeCard() {
    if (!this.card.classList.contains('-active')) return;
    this.card.classList.remove('-active');
    this.cardRender.innerHTML = '';
    this.colorize(0);
  }

  openCard(event) {
    const selectedId = event.features[0].properties.lun_building_id;
    const zoomLevel = this.map.getZoom();
    this.fetchBuilding(selectedId);
    this.colorize(selectedId);
    sendEvent(window.eventCategory, `map_zoom_${zoomLevel}`, 'building_click');
  }

  getMarker(id) {
    return document.querySelector(`.ContactsLocation-marker[data-sales-center-id="${id}"]`);
  }

  closeContactsCard() {
    if (this.currentSalesCenterId === 0) return;
    this.getMarker(this.currentSalesCenterId).classList.remove('-active');
    this.contactsCard.innerHTML = '';
    this.opportunities.innerHTML = '';
    this.opportunities.classList.add('hidden');
    this.contactsCard.classList.add('hidden');
    this.currentSalesCenterId = 0;
  }

  openContactsCard() {
    if (this.currentSalesCenterId === 0) return;
    const zoomLevel = this.map.getZoom();
    this.getMarker(this.currentSalesCenterId).classList.add('-active');
    this.opportunities.innerHTML = this.salesCenterData.opportunities;
    window.params.opportunitiesParams = this.salesCenterData.opportunitiesParams;
    this.contactsCard.classList.remove('hidden');
    this.opportunities.classList.remove('hidden');
    this.initBuildingChips();
    initDeveloperQuestion(this.contactsCard);
    sendEvent(window.eventCategory, `map_zoom_${zoomLevel}`, 'sales_center_click');
  }

  openOpportunities(id) {
    const activeOpportunities = document.querySelector(`[data-building-opportunities="${id}"]`);
    if (!activeOpportunities) return;
    activeOpportunities.classList.remove('hidden');
    contactsOpportunities(
      activeOpportunities.querySelector('[data-calendar]'),
      window.params.opportunitiesParams[id],
      window.params.mutedDates,
    );
  }

  initBuildingChips() {
    const chips = this.contactsCard.querySelectorAll('[data-building]');
    const contacts = this.contactsCard.querySelectorAll('[data-building-contacts]');
    const opportunities = this.opportunities.querySelectorAll('[data-building-opportunities]');
    initPhone(this.contactsCard);

    if (chips.length > 0) {
      const activeChip = this.contactsCard.querySelector('[data-building].-active');
      if (activeChip) {
        this.openOpportunities(activeChip.dataset.building);
      }
      [].forEach.call(chips, (chip) => {
        chip.addEventListener('click', () => {
          const id = chip.dataset.building;
          chips.forEach((e) => e.classList.remove('-active'));
          contacts.forEach((e) => e.classList.add('hidden'));
          opportunities.forEach((e) => e.classList.add('hidden'));

          const activeContacts = this.contactsCard.querySelector(
            `[data-building-contacts="${id}"]`,
          );
          chip.classList.add('-active');
          activeContacts.classList.remove('hidden');
          this.openOpportunities(id);
        });
      });
    } else {
      const [id, questionUrl] = Object.entries(this.salesCenterData.developerQuestionUrl)[0];
      window.params.developerQuestionUrl = questionUrl;
      if (!opportunities) return;
      this.openOpportunities(id);
    }
  }

  scrollListener() {
    const rect = this.container.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom >= 0) {
      this.mapInit();
      window.removeEventListener('scroll', this.scrollListener);
    }
  }
}

export default Map;
